"use client";

import { useState, useEffect, useMemo } from "react";
import {
  Control,
  FieldValues,
  Path,
  UseFormRegister,
  UseFormSetValue,
  PathValue,
  useWatch,
  UseFormTrigger,
} from "react-hook-form";
import { BiMinus, BiPlus } from "react-icons/bi";

import Button, { ButtonStyle } from "components/Button";
import { VALIDATION_MESSAGE } from "constants/validation";
import { cn } from "utils/cn";
import { forceValidation } from "utils/form.util";
/**
 * ## カウンターコンポーネント
 *
 * ジェネリクスを使用して、汎用的なフォームデータ型に対応したカウンターコンポーネントです。
 *
 * ### Props
 * - `name` (`Path<T>`): フォームフィールド名
 * - `control` (`Control<T>`): react-hook-formのコントロール
 * - `setValue` (`(name: Path<T>, value: number) => void`): フォームフィールドの値を設定する関数
 * - `register` (`UseFormRegister<T>`): react-hook-formのregister関数
 * - `max` (`number`, default: 100): 最大値
 * - `defaultCount` (`number`, default: 1): 初期値
 *
 * ### ジェネリクス
 * - `T`: フォームデータの型
 *
 * ### テストケース
 * - 初期値が設定されていることを確認する
 * - +ボタンをクリックするとカウントが増加する
 * - -ボタンをクリックするとカウントが減少する
 * - カウントが1以下に減少しない
 * - カウントがmax以上に増加しない
 * - カウントがmaxのとき+ボタンが無効化される
 * - カウントが1のとき-ボタンが無効化される
 *
 */
interface NumberOfUnitsProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  register: UseFormRegister<T>;
  max?: number;
  defaultCount?: number;
  maxValidation?: number;
  isDecreaseToZero?: boolean;
  trigger?: UseFormTrigger<T>;
  className?: string;
}

export const NumberOfUnits = <T extends FieldValues>({
  name,
  max = 100,
  defaultCount = 1,
  setValue,
  control,
  register,
  maxValidation,
  isDecreaseToZero = false,
  trigger,
  className,
}: NumberOfUnitsProps<T>) => {
  // watch the value of the input
  const currentCount = useWatch({ control, name });
  const [count, setCount] = useState(defaultCount);

  const validationOptions = useMemo(() => {
    return {
      valueAsNumber: true,
      ...(maxValidation && {
        max: { value: maxValidation, message: VALIDATION_MESSAGE.TOO_LARGE_NUMBER(maxValidation) },
      }),
    };
  }, [maxValidation]);

  // local値変わった時に、formの値も変わるようにする
  useEffect(() => {
    const isDifferentValue = count !== defaultCount;
    setValue(name, Number(count) as PathValue<T, Path<T>>, {
      shouldDirty: isDifferentValue,
      shouldValidate: isDifferentValue,
    });
  }, [name, count, setValue, defaultCount]);

  const canDecrease = isDecreaseToZero ? count > 0 : count > 1;

  const decreaseCount = () => {
    if (canDecrease) {
      setCount(count - 1);

      forceValidation({ trigger, name });
    }
  };

  const increaseCount = () => {
    if (count < max) {
      setCount(count + 1);

      forceValidation({ trigger, name });
    }
  };

  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCount(() => {
      const value = Number(e.target.value);
      const decreaseNum = isDecreaseToZero ? 0 : 1;

      if (isNaN(value)) return decreaseNum;

      if (value > max) return max;

      if (value < decreaseNum) return decreaseNum;

      return value;
    });
  };

  if (currentCount === undefined || count === undefined || name === undefined) return null;

  // 数字の場合は、スクロールしたら、数字が増減する現象を防ぐ。
  const onWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (event.target instanceof HTMLInputElement) {
      const inputElement = event.target;
      if (inputElement.type === "number") {
        inputElement.blur();
      }
    }
  };

  return (
    <div className={cn("flex items-center", className)}>
      <div className="w-12">
        <Button
          width="w-12"
          text={<BiMinus />}
          style={ButtonStyle.WHITE}
          isDisabled={!canDecrease}
          onClick={decreaseCount}
          minWidth="w-12"
          aria-label="minus"
        />
      </div>
      <input
        {...register(name, validationOptions)}
        className="m-2 h-12 w-12 rounded border border-input-normal text-center hover:border-black focus:border-link focus:bg-link-light focus:shadow-input focus:outline-none"
        type="number"
        value={`${currentCount}`}
        onChange={(e) => handleCountChange(e)}
        onWheel={onWheel}
        aria-label="count"
      />
      <div className="w-12">
        <Button
          text={<BiPlus />}
          width="w-12"
          minWidth="w-12"
          style={ButtonStyle.WHITE}
          isDisabled={count >= max}
          onClick={increaseCount}
          aria-label="plus"
        />
      </div>
    </div>
  );
};

export default NumberOfUnits;
